import { useContext, useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { v4 } from "uuid";

import type {
  SaveSearchReasoningV2Request,
  SuccessPatientIndexSearchResultV2,
  TooManyMatchesPatientIndexSearchResultV2,
} from "@thalamos/aspire-common";
import { canViewPatientList } from "@thalamos/aspire-common";

import type { CriteriaType } from "./CollectCriteria.js";
import { CollectCriteria } from "./CollectCriteria.js";
import type {
  CollectCriteriaDemographicsProps,
  ValidationSchema,
  ValidationSchema as ValidationSchemaDemographics,
} from "./CollectCriteriaDemographics.js";
import type { CollectCriteriaNhsNumberProps } from "./CollectCriteriaNhsNumber.js";
import type { CollectCriteriaPdsDemographicsProps } from "./CollectCriteriaPdsDemographics.js";
import { CreatePatient } from "./CreatePatient.js";
import { Footer } from "./Footer.js";
import { Header } from "./Header.js";
import { PdsDemographicsExplainer } from "./PdsDemographicsExplainer.js";
import { SearchReasoningDialog } from "./SearchReasoningDialog.js";
import { SearchResults } from "./SearchResults.js";
import { ageRangeToDateRange } from "./util.js";
import { api } from "~/api.js";
import {
  Banner,
  BannerList,
  Button,
  ButtonIcon,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { HorizontalLine } from "~/components/form/HorizontalLine.js";
import { LoggedInUserContext } from "~/Contexts.js";
import { useDialogs } from "~/hooks/dialogs/index.js";
import { patientLandingRoute } from "~/pages/CareHomeRegister/patientLandingRoute.js";
import { routeFns } from "~/routePaths.js";

export type PatientSearchProps = Record<string, never>;

/**
 * Contains a set of search results, and the handlers to act upon them.
 * This combination allows us to have different journeys depending on
 * whether the search was done by demographics or NHS Number.
 */
type SearchResultsAndHandlers = {
  searchResults:
    | "loading"
    | TooManyMatchesPatientIndexSearchResultV2
    | SuccessPatientIndexSearchResultV2;

  /**
   * The function we will call if we want to continue with a search result.
   * @param index The index of the selected search result.
   * @returns
   */
  onContinue?: (index: number) => void;

  /**
   * Called when the user wants to create a patient.
   * This should return the demographics to pre-fill the create patient form with.
   * @returns The demographics to pre-fill the create patient form with.
   */
  onCreate?: () => ValidationSchemaDemographics | undefined;
};

function SupersededPatientPopup({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) {
  return (
    <PopupDialog open={open} onClose={() => setOpen(false)}>
      <PopupDialogTitle
        titleText={`NHS number has been updated`}
        closeDialog={() => setOpen(false)}
      />
      <Typography>
        The NHS number you searched for has been replaced (aka superseded) in
        NHS national systems - the patient should be informed of this if they
        have not been already.
      </Typography>
      <Banner
        sx={{ mt: "1em" }}
        bannerType={BannerList.WARNING}
        title="Results may be unexpected - please review
        carefully before proceeding."
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: "1em",
        }}
      >
        <Button
          variant="outlined"
          label={"Close"}
          onClick={() => setOpen(false)}
        />
      </Box>
    </PopupDialog>
  );
}

function PdsSearchFailedPopup({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) {
  return (
    <PopupDialog open={open} onClose={() => setOpen(false)}>
      <PopupDialogTitle
        titleText={`PDS Search Failed`}
        closeDialog={() => setOpen(false)}
      />
      <Typography>
        Search of PDS failed - only local results shown. Please try again later.
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: "1em",
        }}
      >
        <Button
          variant="outlined"
          label={"Close"}
          onClick={() => setOpen(false)}
        />
      </Box>
    </PopupDialog>
  );
}

export const PatientSearch = (_props: PatientSearchProps) => {
  const dialogs = useDialogs();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userContext = useContext(LoggedInUserContext);

  // Only the patient list is honoured, and only while the caller can view it:
  // otherwise both the add and the return trip have nowhere to land.
  const returnToPatientList =
    searchParams.get("redirectTo") === routeFns.patientList() &&
    canViewPatientList(userContext?.user);

  const canSearchByNhsNumber = useSearchByNhsNumberEnabled();
  const canSearchInPds = usePdsEnabled();
  const canUsePhoneticNameMatch = usePhoneticSearchEnabled();

  const rioInstance = useRioInstance();

  // Whether we are creating a patient having failed to find one.
  const [isCreatingPatient, setIsCreatingPatient] = useState(false);

  // This is where we store the search result from the API
  const [searchResultsAndHandlers, setSearchResultsAndHandlers] = useState<
    SearchResultsAndHandlers | undefined
  >(undefined);

  // This is where we store the last searched demographics
  const [searchedDemographics, setSearchedDemographics] = useState<
    ValidationSchema | undefined
  >(undefined);

  // This is where we store the index of the selected search
  const [searchResultIndexSelected, setSearchResultIndexSelected] = useState<
    number | null
  >(null);

  const [showSupersededAlert, setShowSupersededAlert] = useState(false);
  const [showPdsFailedAlert, setShowPdsFailedAlert] = useState(false);
  const [showPdsDemographicsExplainer, setShowPdsDemographicsExplainer] =
    useState(false);

  // Which type of criteria the user is currently using to search
  const [criteriaType, setCriteriaType] = useState<CriteriaType>(
    canSearchByNhsNumber ? "nhs-number" : "demographics",
  );

  // Reset the search results when the criteria type changes
  useEffect(() => {
    setSearchResultsAndHandlers(undefined);
    setSearchResultIndexSelected(null);
    setShowPdsDemographicsExplainer(false);
  }, [criteriaType]);

  // Handler for criteria type changes
  const handleCriteriaTypeChange = (newCriteriaType: CriteriaType) => {
    setCriteriaType(newCriteriaType);
  };

  // Calculate the overall state of the page
  const state =
    searchResultsAndHandlers === undefined
      ? "search"
      : searchResultsAndHandlers.searchResults === "loading"
        ? "searching"
        : searchResultsAndHandlers.searchResults.searchOutcome ===
            "too-many-matches"
          ? "too-many-matches"
          : searchResultsAndHandlers.searchResults.pdsResult ===
              "too-many-matches"
            ? "too-many-matches-pds"
            : searchResultsAndHandlers.searchResults.matchedPatients.length ===
                0
              ? "results-not-found"
              : searchResultIndexSelected === null
                ? "results-found"
                : "result-selected";

  const onContinueFn =
    (data: SuccessPatientIndexSearchResultV2) => async (index: number) => {
      const selectedPatient = data.matchedPatients[index];
      if (selectedPatient === undefined) return;
      const selectedSearchResultId = selectedPatient.resultId;

      let searchReasoning: SaveSearchReasoningV2Request;
      if (selectedPatient.searchReasoningNeeded) {
        const response = await dialogs.open(SearchReasoningDialog);
        if (response.type === "cancelled") {
          return;
        }

        searchReasoning = {
          reason: response.reasoning.label,
          reasonDescription:
            response.reasoning.reason === "other"
              ? response.reasoning.description
              : undefined,
        };
      } else {
        searchReasoning = {
          searchReasoningAlreadyProvided: true,
        };
      }

      // Record the search reason
      const result = await api.patients.addSearchV2Reasoning(
        selectedSearchResultId,
        searchReasoning,
      );

      if (result.status === 200) {
        const chosenPatientId =
          result.data.type === "suggested-merge"
            ? undefined
            : result.data.patientId;

        // The reasoning call above is the only source of the patient id.
        if (returnToPatientList && chosenPatientId !== undefined) {
          await addToPatientListAndReturn(
            chosenPatientId,
            selectedPatient.patientDemographics.name,
          );
          return;
        }

        if (result.data.type === "patient-page") {
          if (result.data.externalLinkToSync) {
            navigate(
              routeFns.patientSyncExternalLink(
                result.data.patientId,
                result.data.externalLinkToSync,
              ),
            );
          } else {
            navigate(
              patientLandingRoute(userContext?.user, result.data.patientId),
            );
          }
        } else if (result.data.type === "s-flag-setup") {
          navigate(routeFns.patientEdit(result.data.patientId));
        } else {
          alert("Unsupported response type - " + result.data.type);
        }
      } else {
        renderErrorToast({
          message:
            "Failed to record search reasoning - please try your search again",
        });
      }
    };

  // Search for patients using NHS Number
  const searchByNhsNumber: CollectCriteriaNhsNumberProps["onSubmit"] = async (
    values,
  ) => {
    setSearchResultsAndHandlers({ searchResults: "loading" });

    // Look for an existing aspire patient with this NHS Number
    const { data } = await api.patients.searchV2(v4(), {
      type: "nhs-number",
      nhsNumber: values.nhsNumber,
      patientSearchVariant: "normal",
    });

    if (data.searchOutcome === "too-many-matches") {
      setSearchResultsAndHandlers({ searchResults: data });
      return;
    }

    // TODO: preserve the Rio journey here in case PDS not enabled
    // If PDS _was_ enabled and there are no results, looking in Rio is pointless
    // and we should just go to patient creation.
    if (data.matchedPatients.length === 0 && rioInstance && !canSearchInPds) {
      // Create a claimed click-through token
      const token = v4();
      const tokenResponse = await api.rio.clickThrough.createAndClaimToken({
        token,
        externalSystemType: "rio",
        externalSystemId: rioInstance.instanceId,
        externalPatientIdType: "nhs.number",
        externalPatientIdValue: values.nhsNumber,
      });

      if (tokenResponse.status === 204) {
        // Redirect to the click-through flow to redeem the token and continue the journey
        navigate(routeFns.patientCreateFromExternalId(token));
      }
    }

    if (data.nhsNumberWasSuperseded) {
      setShowSupersededAlert(true);
    }

    if (data.pdsResult === "error") {
      setShowPdsFailedAlert(true);
    }

    setSearchResultsAndHandlers({
      searchResults: data,
      onContinue: onContinueFn(data),
      onCreate: () => {
        // We don't have any information about demographics to
        // pre-fill the create patient form with.
        return undefined;
      },
    });
  };

  // Search for patients using Demographics
  const searchByDemographics: CollectCriteriaDemographicsProps["onSubmit"] =
    async (values: ValidationSchema) => {
      setSearchedDemographics(values);
      setSearchResultsAndHandlers({ searchResults: "loading" });

      const { data } = await api.patients.searchV2(v4(), {
        type: "demographics",
        enablePhoneticMatchOnSurname: values.usePhoneticSurnameMatch,
        enablePhoneticMatchOnGivenName: values.usePhoneticGivenNameMatch,

        given: values.givenName ?? undefined,
        surname: values.familyName!,
        birthdate: values.dateOfBirthIsExact
          ? values.dateOfBirthExact
          : ageRangeToDateRange(values.ageApprox),
        patientSearchVariant: "normal",
      });

      if (data.searchOutcome === "too-many-matches") {
        setSearchResultsAndHandlers({ searchResults: data });
        return;
      }

      setSearchResultsAndHandlers({
        searchResults: data,
        onContinue: onContinueFn(data),
        onCreate: () => {
          // We will use these demographics to pre-fill the create
          // patient form with, if they are not happy with the search results.
          return values;
        },
      });
    };

  // Search for patients using PDS Demographics
  const searchByPdsDemographics: CollectCriteriaPdsDemographicsProps["onSubmit"] =
    async (values) => {
      setSearchResultsAndHandlers({ searchResults: "loading" });

      const { data } = await api.patients.searchV2(v4(), {
        type: "pds-demographics",

        postcode: values.postcode ?? undefined,
        given: values.givenName ?? undefined,
        surname: values.familyName!,
        birthdate: values.dateOfBirthIsExact
          ? values.dateOfBirthExact
          : ageRangeToDateRange(values.ageApprox),
        patientSearchVariant: "normal",
      });

      if (data.searchOutcome === "too-many-matches") {
        setSearchResultsAndHandlers({ searchResults: data });
        return;
      }

      if (data.pdsResult === "error") {
        setShowPdsFailedAlert(true);
      }

      setSearchResultsAndHandlers({
        searchResults: data,
        onContinue: onContinueFn(data),
        onCreate: () => {
          // We will use these demographics to pre-fill the create
          // patient form with, if they are not happy with the search results.
          return {
            familyName: values.familyName,
            givenName: values.givenName,
            dateOfBirthExact: values.dateOfBirthExact,
            dateOfBirthIsExact: values.dateOfBirthIsExact,

            usePhoneticGivenNameMatch: false,
            usePhoneticSurnameMatch: false,

            ageApprox: values.ageApprox,
          };
        },
      });
    };

  // Show the create patient page
  const onCreate = async () => {
    setIsCreatingPatient(true);
  };

  const addToPatientListAndReturn = async (
    patientId: string,
    patientName?: string,
  ) => {
    const added = await api.patientList.add(patientId);
    if (added.status !== 204) {
      renderErrorToast({
        message: "Failed to add the patient to your list",
      });
      return;
    }
    renderSuccessToast({
      message: patientName
        ? `${patientName} added to Patient List`
        : "Patient added to Patient List",
    });
    navigate(routeFns.patientList(), { replace: true });
  };

  const tooManyMatches = state === "too-many-matches";
  const tooManyMatchesPds = state === "too-many-matches-pds";

  return isCreatingPatient ? (
    <CreatePatient
      initialValues={searchResultsAndHandlers?.onCreate?.()}
      onCreated={
        returnToPatientList
          ? (patientId) => addToPatientListAndReturn(patientId)
          : undefined
      }
      onCancel={() => {
        setSearchResultsAndHandlers(undefined);
        setSearchResultIndexSelected(null);
        setIsCreatingPatient(false);
      }}
    />
  ) : (
    <Stack
      sx={{
        gap: "1rem",
      }}
    >
      {returnToPatientList && (
        <Button
          sx={{ alignSelf: "flex-start" }}
          label="Go Back"
          startIcon={ButtonIcon.arrowBack}
          variant="text"
          onClick={() => {
            navigate(routeFns.patientList());
          }}
        />
      )}

      <Header />

      <SupersededPatientPopup
        open={showSupersededAlert}
        setOpen={setShowSupersededAlert}
      />

      <PdsSearchFailedPopup
        open={showPdsFailedAlert}
        setOpen={setShowPdsFailedAlert}
      />

      {/* Criteria type selection UI */}
      {criteriaType !== "pds-demographics" ? (
        <>
          <FormControl>
            <RadioGroup
              value={criteriaType}
              onChange={(event) => {
                const value = event.target.value as CriteriaType;
                if (["nhs-number", "demographics"].includes(value)) {
                  handleCriteriaTypeChange(value);
                }
              }}
            >
              {canSearchByNhsNumber && (
                <FormControlLabel
                  data-testid="radio-nhs-number"
                  value="nhs-number"
                  control={<Radio />}
                  label="Search by NHS Number"
                  disabled={["searching", "result-selected"].includes(state)}
                />
              )}
              <FormControlLabel
                data-testid="radio-demographics"
                value="demographics"
                control={<Radio />}
                label="Search by Demographics"
                disabled={["searching", "result-selected"].includes(state)}
              />
            </RadioGroup>
          </FormControl>
          <HorizontalLine />
        </>
      ) : (
        <Box>
          {showPdsDemographicsExplainer && (
            <PdsDemographicsExplainer
              onClose={() => setShowPdsDemographicsExplainer(false)}
            />
          )}

          <Button
            sx={{ mt: "1rem" }}
            label="Go Back"
            startIcon={ButtonIcon.arrowBack}
            variant="text"
            onClick={() => handleCriteriaTypeChange("nhs-number")}
          />
          <Banner
            sx={{ mt: "1rem", mb: "1rem" }}
            bannerType={BannerList.INFO}
            title="PDS Search"
            body="If you don't know the NHS number, you can search PDS with demographics below. PDS is the patient demographics service, also known as the Spine, and is the national electronic record of NHS patient demographics data."
            leftButtonLabel="View Search Criteria"
            onLeftButton={() => setShowPdsDemographicsExplainer(true)}
          />
        </Box>
      )}

      <CollectCriteria
        canUsePhoneticNameMatch={canUsePhoneticNameMatch}
        onSubmitNhsNumber={searchByNhsNumber}
        onSubmitDemographics={searchByDemographics}
        onSubmitPdsDemographics={searchByPdsDemographics}
        criteriaType={criteriaType}
        disabled={["searching", "result-selected"].includes(state)}
        lastSearchedDemographics={searchedDemographics}
      >
        {({ onClear }) => {
          return (
            <>
              <SearchResults
                searchResult={
                  tooManyMatches
                    ? "too-many-matches"
                    : tooManyMatchesPds
                      ? "too-many-matches-pds"
                      : (searchResultsAndHandlers?.searchResults as SuccessPatientIndexSearchResultV2)
                }
                onSelectedIndexChanged={setSearchResultIndexSelected}
                canSearchByPdsDemographics={canSearchInPds}
                criteriaType={criteriaType}
                setCriteriaType={handleCriteriaTypeChange}
              />

              <Footer
                state={state}
                onContinue={() => {
                  if (searchResultIndexSelected !== null)
                    searchResultsAndHandlers?.onContinue?.(
                      searchResultIndexSelected,
                    );
                }}
                onClear={() => {
                  setSearchedDemographics(undefined);

                  // Clear the Formik fields
                  onClear();

                  // Clear the previous search results
                  setSearchResultsAndHandlers(undefined);
                  setSearchResultIndexSelected(null);
                }}
                onCreate={onCreate}
              />
            </>
          );
        }}
      </CollectCriteria>
    </Stack>
  );
};

const useSearchByNhsNumberEnabled = () => {
  const userContext = useContext(LoggedInUserContext);

  return (
    userContext?.user.sessionOrganisationConfiguration?.nhsNumberEnabled ??
    false
  );
};

const usePdsEnabled = () => {
  const userContext = useContext(LoggedInUserContext);

  return (
    userContext?.user.sessionOrganisationConfiguration?.pdsEnabled ?? false
  );
};

const usePhoneticSearchEnabled = () => {
  const userContext = useContext(LoggedInUserContext);

  return (
    userContext?.user.sessionOrganisationConfiguration
      ?.phoneticPatientIndexSearchEnabled ?? false
  );
};

const useRioInstance = () => {
  const userContext = useContext(LoggedInUserContext);

  return userContext?.user?.sessionOrganisationConfiguration
    ?.rioInstancesEnabled?.[0];
};
